import { Fragment, h } from "harmaja"
import { Link, getNavigator } from "harmaja-router"
import * as L from "lonna"
import * as uuid from "uuid"
import { AccessLevel, Board, EventFromServer, UserSessionInfo, canWrite } from "../../../../common/src/domain"
import { Rect } from "../../../../common/src/geometry"
import { Routes, createBoardAndNavigate } from "../../board-navigation"
import { EditableSpan } from "../../components/EditableSpan"
import { onClickOutside } from "../../components/onClickOutside"
import { BoardState, Dispatch } from "../../store/board-store"
import { CRDTStore } from "../../store/crdt-store"
import { UserSessionState, defaultAccessPolicy } from "../../store/user-session-store"
import { FrameNavigator } from "../frames"
import { ThemeToggle } from "../../theme"
import { SharingModalDialog } from "./SharingModalDialog"
import { TimerView } from "./TimerView"
import { UserInfoView } from "./UserInfoView"

const PRESENCE = ["var(--presence-1)", "var(--presence-2)", "var(--presence-3)", "var(--presence-4)"]
const initialsOf = (nickname: string) =>
    nickname
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()

// Floating board chrome from the design handoff: top-left card (back, logo, name,
// frames chip, saved indicator) and top-right cluster (presence, timer, theme,
// export, share, user).
export function BoardViewHeader({
    usersOnBoard,
    board,
    accessLevel,
    sessionState,
    dispatch,
    modalContent,
    eventsFromServer,
    viewRect,
    online,
    boardState,
    crdtStore,
    frames,
    exportPng,
}: {
    usersOnBoard: L.Property<UserSessionInfo[]>
    board: L.Property<Board>
    accessLevel: L.Property<AccessLevel>
    sessionState: L.Property<UserSessionState>
    dispatch: Dispatch
    modalContent: L.Atom<any>
    eventsFromServer: L.EventStream<EventFromServer>
    viewRect: L.Property<Rect>
    online: L.Property<boolean>
    boardState: L.Property<BoardState>
    crdtStore: CRDTStore
    frames: FrameNavigator
    exportPng: () => void
}) {
    const editingAtom = L.atom(false)
    const nameAtom = L.atom(
        L.view(board, (board) => board.name || ""),
        (newName) => dispatch({ action: "board.rename", boardId: board.get()!.id, name: newName }),
    )
    const navigator = getNavigator<Routes>()
    function makeCopy() {
        const newBoard = {
            id: uuid.v4(),
            name: `${nameAtom.get()} copy`,
            templateId: board.get()!.id,
            accessPolicy: defaultAccessPolicy(sessionState.get(), false),
        }
        createBoardAndNavigate(newBoard, dispatch, navigator, eventsFromServer)
    }

    const saved = L.view(boardState, (s) => ({
        online: s.status === "online",
        pending: s.queue.length + s.sent.length,
    }))

    const framesOpen = L.atom(false)
    const framesEl = L.atom<HTMLElement | null>(null)
    onClickOutside(framesEl, () => framesOpen.set(false))
    const frameCount = L.view(frames.frames, (f) => f.length)

    return (
        <div className="board-chrome">
            <div className="top-left-card">
                <Link<Routes> href="/" className="back" title="All boards">
                    ←
                </Link>
                <span className="logo-mark">L</span>
                {L.view(accessLevel, (l) =>
                    canWrite(l) ? (
                        <EditableSpan className="board-name" value={nameAtom} editingThis={editingAtom} />
                    ) : (
                        <span className="board-name">{nameAtom}</span>
                    ),
                )}
                {L.view(frameCount, (n) =>
                    n === 0 ? null : (
                        <span className="frames-wrap" ref={framesEl.set}>
                            <span className="chip" onClick={() => framesOpen.modify((o) => !o)}>
                                {n} {n === 1 ? "frame" : "frames"}
                            </span>
                            {L.view(framesOpen, (open) =>
                                open ? (
                                    <div className="frames-popup">
                                        <a
                                            className="present"
                                            onClick={() => {
                                                framesOpen.set(false)
                                                frames.startPresenting()
                                            }}
                                        >
                                            ▶ Present
                                        </a>
                                        {L.view(frames.frames, frames.current, (fs, cur) => (
                                            <ul>
                                                {fs.map((f, i) => (
                                                    <li
                                                        className={i === cur ? "active" : ""}
                                                        onClick={() => {
                                                            framesOpen.set(false)
                                                            frames.goTo(i)
                                                        }}
                                                    >
                                                        {i + 1}. {f.text || "Untitled"}
                                                    </li>
                                                ))}
                                            </ul>
                                        ))}
                                    </div>
                                ) : null,
                            )}
                        </span>
                    ),
                )}
                {L.view(saved, (s) =>
                    s.online && s.pending === 0 ? (
                        <span className="saved ok">
                            <span className="dot" />
                            Saved
                        </span>
                    ) : s.online ? (
                        <span className="saved pending">
                            <span className="dot" />
                            Saving…
                        </span>
                    ) : (
                        <span className="saved offline">
                            <span className="dot" />
                            Offline{s.pending > 0 ? ` — ${s.pending} queued` : ""}
                        </span>
                    ),
                )}
            </div>
            <div className="top-right-cluster">
                <span className="presence">
                    {L.view(usersOnBoard, (users) => (
                        <>
                            {users.slice(0, 4).map((u, i) => (
                                <span
                                    className="avatar"
                                    title={u.nickname}
                                    style={{ background: PRESENCE[i % PRESENCE.length] }}
                                >
                                    {initialsOf(u.nickname)}
                                </span>
                            ))}
                            {users.length > 4 ? <span className="avatar more">+{users.length - 4}</span> : null}
                        </>
                    ))}
                </span>
                <TimerView board={board} dispatch={dispatch} eventsFromServer={eventsFromServer} />
                <ThemeToggle />
                <button className="btn ghost" onClick={exportPng} title="Export as PNG (⇧⌘E)">
                    Export
                </button>
                <button className="btn ghost" onClick={makeCopy} title="Duplicate this board">
                    Copy
                </button>
                <button
                    className="btn primary"
                    onClick={() =>
                        modalContent.set(
                            <SharingModalDialog
                                {...{
                                    board,
                                    sessionState,
                                    dismiss: () => modalContent.set(null),
                                    dispatch,
                                }}
                            />,
                        )
                    }
                >
                    Share
                </button>
                <UserInfoView
                    state={sessionState}
                    usersOnBoard={usersOnBoard}
                    dispatch={dispatch}
                    modalContent={modalContent}
                />
            </div>
        </div>
    )
}
